import React, { useRef, useEffect, useState, useCallback } from "react";

/* ─── Icons (inline SVG to avoid react-icons dep issues) ─── */
const Icon = ({ d, size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d={d} />
    </svg>
);
const ICONS = {
    bold:        "M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z",
    italic:      "M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z",
    underline:   "M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z",
    ul:          "M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z",
    ol:          "M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-8v2h14V3H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z",
    alignLeft:   "M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z",
    alignCenter: "M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z",
    alignRight:  "M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z",
    link:        "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z",
    quote:       "M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z",
    eraser:      "M16.24 3.56l4.95 4.94c.78.79.78 2.05 0 2.84L12 20.53a4.008 4.008 0 01-5.66 0L2.81 17c-.78-.79-.78-2.05 0-2.84l10.6-10.6c.79-.78 2.05-.78 2.83 0zM4.22 15.58l3.54 3.53c.78.79 2.04.79 2.83 0l3.53-3.53-4.95-4.95-4.95 4.95z",
    rtl:         "M10 10v5h2V4h2v11h2V4h2V2h-8C7.79 2 6 3.79 6 6s1.79 4 4 4zm-2 7v-3l-4 4 4 4v-3h12v-2H8z",
    ltr:         "M9 10v5h2V4h2v11h2V4h2V2H9C6.79 2 5 3.79 5 6s1.79 4 4 4zm12 7H5v-3l-4 4 4 4v-3h16v-2z",
};

/* ─── Allowed tags whitelist ─── */
const ALLOWED_TAGS = new Set(["P","B","STRONG","I","EM","U","UL","OL","LI","A","H1","H2","H3","BLOCKQUOTE","BR","SPAN","DIV"]);

/* ─── HTML sanitizer ─── */
const sanitize = (html) => {
    const wrap = document.createElement("div");
    wrap.innerHTML = html || "";
    const walk = (node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (!ALLOWED_TAGS.has(node.tagName)) {
                const parent = node.parentNode;
                while (node.firstChild) parent.insertBefore(node.firstChild, node);
                parent.removeChild(node);
                return;
            }
            [...node.attributes].forEach((attr) => {
                const keep =
                    (node.tagName === "A" && ["href","target","rel"].includes(attr.name)) ||
                    (["dir","style"].includes(attr.name));
                if (!keep) node.removeAttribute(attr.name);
            });
            if (node.tagName === "A") {
                node.setAttribute("target", "_blank");
                node.setAttribute("rel", "noopener noreferrer");
            }
        }
        let child = node.firstChild;
        while (child) {
            const next = child.nextSibling;
            walk(child);
            child = next;
        }
    };
    walk(wrap);
    return wrap.innerHTML;
};

/* ─── Detect if text is predominantly RTL (Arabic/Hebrew etc.) ─── */
const isRTLText = (text) => {
    const rtlChars = (text.match(/[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g) || []).length;
    const ltrChars = (text.match(/[A-Za-z\u00C0-\u024F]/g) || []).length;
    return rtlChars > ltrChars;
};

/* ─── Save / restore selection ─── */
const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    return sel.getRangeAt(0).cloneRange();
};
const restoreSelection = (range) => {
    if (!range) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
};

/* ─── Direct DOM bold/italic/underline toggle (works in all block contexts) ─── */
const INLINE_TAGS = { bold: "B", italic: "I", underline: "U" };

/**
 * Check if every text node in `range` is already wrapped by `tagName`.
 */
const isFullyWrapped = (range, tagName) => {
    const frag = range.cloneContents();
    const textNodes = [];
    const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);
    if (textNodes.length === 0) return false;
    return textNodes.every((tn) => {
        let p = tn.parentNode;
        while (p) {
            if (p.nodeName === tagName) return true;
            p = p.parentNode;
        }
        return false;
    });
};

/**
 * Unwrap all `tagName` elements inside a DOM node.
 */
const unwrapTag = (container, tagName) => {
    container.querySelectorAll(tagName).forEach((el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
    });
};

/**
 * Toggle inline format (bold/italic/underline) via direct DOM manipulation.
 * Reliable in all block contexts — paragraph, h1, h2, h3, etc.
 */
const toggleInlineFormat = (tagName) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    if (range.collapsed) {
        // No selection — let execCommand handle the "type-ahead" state
        document.execCommand(tagName.toLowerCase() === "b" ? "bold" :
            tagName.toLowerCase() === "i" ? "italic" : "underline", false, null);
        return;
    }

    if (isFullyWrapped(range, tagName)) {
        // ── REMOVE: extract contents, unwrap all matching tags, re-insert ──
        const frag = range.extractContents();
        unwrapTag(frag, tagName);
        // Also unwrap nested (e.g. <b><b>text</b></b>)
        const temp = document.createElement("span");
        temp.appendChild(frag);
        unwrapTag(temp, tagName);
        range.insertNode(temp);
        // Flatten the wrapper span
        const parent = temp.parentNode;
        while (temp.firstChild) parent.insertBefore(temp.firstChild, temp);
        parent.removeChild(temp);
    } else {
        // ── APPLY: wrap selection in tagName ──
        const wrapper = document.createElement(tagName);
        try {
            range.surroundContents(wrapper);
        } catch {
            // surroundContents fails for partial selections across block boundaries
            // fall back to extractContents + wrap
            const frag = range.extractContents();
            wrapper.appendChild(frag);
            range.insertNode(wrapper);
        }
    }

    // Restore selection
    sel.removeAllRanges();
    sel.addRange(range);
};

/* ─── execCommand wrapper — uses direct DOM for inline, execCommand for block/list ─── */
const execFmt = (command, value = null) => {
    const inlineMap = { bold: "B", italic: "I", underline: "U" };
    if (inlineMap[command]) {
        toggleInlineFormat(inlineMap[command]);
    } else if (value !== null) {
        document.execCommand(command, false, value);
    } else {
        document.execCommand(command, false, null);
    }
};

/* ─── Main component ─── */
const RichTextEditor = ({ value, onChange, placeholder }) => {
    const editorRef = useRef(null);
    const isFocused = useRef(false);
    const savedRange = useRef(null);

    const [formatBlock, setFormatBlock] = useState("p");
    const [active, setActive] = useState({ bold: false, italic: false, underline: false });
    const [dir, setDir] = useState("ltr");

    /* Sync incoming value only when not focused */
    useEffect(() => {
        if (isFocused.current || !editorRef.current) return;
        const incoming = value || "";
        if (editorRef.current.innerHTML !== incoming) {
            editorRef.current.innerHTML = incoming;
            // Auto-detect direction from content
            const text = editorRef.current.innerText || "";
            if (text) setDir(isRTLText(text) ? "rtl" : "ltr");
        }
    }, [value]);

    /* Check if cursor/selection has a given ancestor tag — works in all block contexts */
    const hasAncestor = useCallback((tagName) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return false;
        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        while (node && node !== editorRef.current) {
            if (node.nodeName === tagName) return true;
            node = node.parentNode;
        }
        if (!sel.isCollapsed) {
            try { return isFullyWrapped(sel.getRangeAt(0), tagName); } catch (_) {}
        }
        return false;
    }, []);

    /* Update toolbar state — DOM-walk based, reliable in paragraph + all heading blocks */
    const updateToolbarState = useCallback(() => {
        try {
            setActive({
                bold:      hasAncestor("B") || hasAncestor("STRONG"),
                italic:    hasAncestor("I") || hasAncestor("EM"),
                underline: hasAncestor("U"),
            });
            const block = document.queryCommandValue("formatBlock") || "p";
            setFormatBlock(block.toLowerCase().replace(/^<|>$/g, ""));
        } catch (_) {}
    }, [hasAncestor]);

    useEffect(() => {
        const handler = () => { if (isFocused.current) updateToolbarState(); };
        document.addEventListener("selectionchange", handler);
        return () => document.removeEventListener("selectionchange", handler);
    }, [updateToolbarState]);

    /* Toolbar button click: save range → run → restore → fire onChange */
    const runCmd = useCallback((command, value = null) => {
        editorRef.current?.focus();
        restoreSelection(savedRange.current);
        execFmt(command, value);
        updateToolbarState();
        const html = editorRef.current?.innerHTML || "";
        onChange?.(html);
    }, [onChange, updateToolbarState]);

    const handleInput = useCallback(() => {
        const html = editorRef.current?.innerHTML || "";
        // Auto-detect direction from typed text
        const text = editorRef.current?.innerText || "";
        if (text.length > 0) {
            const detected = isRTLText(text) ? "rtl" : "ltr";
            if (detected !== dir) setDir(detected);
        }
        onChange?.(html);
    }, [onChange, dir]);

    /* On paste: strip unsafe HTML */
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const clipboard = e.clipboardData || window.clipboardData;
        const html = clipboard.getData("text/html") || "";
        const text = clipboard.getData("text/plain") || "";
        const content = html ? sanitize(html) : text.replace(/\n/g, "<br/>");
        document.execCommand("insertHTML", false, content);
        onChange?.(editorRef.current?.innerHTML || "");
    }, [onChange]);

    const handleFocus = useCallback(() => { isFocused.current = true; }, []);

    const handleBlur = useCallback(() => {
        isFocused.current = false;
        const raw = editorRef.current?.innerHTML || "";
        const clean = sanitize(raw);
        if (clean !== raw && editorRef.current) editorRef.current.innerHTML = clean;
        onChange?.(clean);
    }, [onChange]);

    /* Save selection on every keyup + mouseup so toolbar always has a valid range */
    const handleSelectionSave = useCallback(() => {
        savedRange.current = saveSelection();
        updateToolbarState();
    }, [updateToolbarState]);

    /* Toggle direction */
    const toggleDir = useCallback(() => {
        const next = dir === "ltr" ? "rtl" : "ltr";
        setDir(next);
        if (editorRef.current) editorRef.current.setAttribute("dir", next);
    }, [dir]);

    /* Block format select */
    const handleBlockFormat = useCallback((e) => {
        const tag = e.target.value;
        setFormatBlock(tag);
        editorRef.current?.focus();
        restoreSelection(savedRange.current);
        document.execCommand("formatBlock", false, tag);
        onChange?.(editorRef.current?.innerHTML || "");
    }, [onChange]);

    /* Insert link */
    const handleLink = useCallback(() => {
        editorRef.current?.focus();
        restoreSelection(savedRange.current);
        const url = window.prompt("Enter URL:");
        if (url) {
            document.execCommand("createLink", false, url);
            onChange?.(editorRef.current?.innerHTML || "");
        }
    }, [onChange]);

    return (
        <div className="rte-container" style={styles.container}>
            {/* ── Toolbar ── */}
            <div style={styles.toolbar}>
                {/* Block format */}
                <select
                    style={styles.select}
                    value={formatBlock}
                    onChange={handleBlockFormat}
                    onMouseDown={(e) => { savedRange.current = saveSelection(); }}
                >
                    <option value="p">Paragraph</option>
                    <option value="h1">H1</option>
                    <option value="h2">H2</option>
                    <option value="h3">H3</option>
                </select>

                <div style={styles.divider} />

                {/* Bold */}
                <ToolBtn
                    active={active.bold}
                    title="Bold (Ctrl+B)"
                    onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("bold"); }}
                ><Icon d={ICONS.bold} /></ToolBtn>

                {/* Italic */}
                <ToolBtn
                    active={active.italic}
                    title="Italic (Ctrl+I)"
                    onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("italic"); }}
                ><Icon d={ICONS.italic} /></ToolBtn>

                {/* Underline */}
                <ToolBtn
                    active={active.underline}
                    title="Underline (Ctrl+U)"
                    onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("underline"); }}
                ><Icon d={ICONS.underline} /></ToolBtn>

                <div style={styles.divider} />

                {/* Lists */}
                <ToolBtn title="Bullet List"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("insertUnorderedList"); }}
                ><Icon d={ICONS.ul} /></ToolBtn>
                <ToolBtn title="Numbered List"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("insertOrderedList"); }}
                ><Icon d={ICONS.ol} /></ToolBtn>

                <div style={styles.divider} />

                {/* Alignment */}
                <ToolBtn title="Align Left"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("justifyLeft"); }}
                ><Icon d={ICONS.alignLeft} /></ToolBtn>
                <ToolBtn title="Align Center"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("justifyCenter"); }}
                ><Icon d={ICONS.alignCenter} /></ToolBtn>
                <ToolBtn title="Align Right"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("justifyRight"); }}
                ><Icon d={ICONS.alignRight} /></ToolBtn>

                <div style={styles.divider} />

                {/* Link */}
                <ToolBtn title="Insert Link"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); }}
                         onClick={handleLink}
                ><Icon d={ICONS.link} /></ToolBtn>

                {/* Blockquote */}
                <ToolBtn title="Blockquote"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("formatBlock", "blockquote"); }}
                ><Icon d={ICONS.quote} /></ToolBtn>

                {/* Clear format */}
                <ToolBtn title="Clear Formatting"
                         onMouseDown={(e) => { e.preventDefault(); savedRange.current = saveSelection(); runCmd("removeFormat"); }}
                ><Icon d={ICONS.eraser} /></ToolBtn>

                <div style={styles.divider} />

                {/* RTL/LTR toggle */}
                <ToolBtn
                    title={dir === "ltr" ? "Switch to RTL (Arabic)" : "Switch to LTR (English)"}
                    active={dir === "rtl"}
                    onMouseDown={(e) => { e.preventDefault(); toggleDir(); }}
                >
                    <Icon d={dir === "ltr" ? ICONS.rtl : ICONS.ltr} />
                    <span style={{ fontSize: 10, marginLeft: 3, fontFamily: "monospace" }}>
            {dir.toUpperCase()}
          </span>
                </ToolBtn>
            </div>

            {/* ── Editor area ── */}
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                dir={dir}
                data-placeholder={placeholder || "Enter description..."}
                style={{ ...styles.editor, direction: dir, textAlign: dir === "rtl" ? "right" : "left" }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onInput={handleInput}
                onKeyUp={handleSelectionSave}
                onMouseUp={handleSelectionSave}
                onPaste={handlePaste}
            />

            <style>{css}</style>
        </div>
    );
};

/* ─── Toolbar button helper ─── */
const ToolBtn = ({ children, active, title, onMouseDown, onClick }) => (
    <button
        type="button"
        title={title}
        onMouseDown={onMouseDown}
        onClick={onClick}
        style={{
            ...styles.btn,
            ...(active ? styles.btnActive : {}),
        }}
    >
        {children}
    </button>
);

/* ─── Inline styles ─── */
const styles = {
    container: {
        border: "1px solid #d1d5db",
        borderRadius: 8,
        overflow: "hidden",
        fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif",
        background: "#fff",
    },
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        padding: "6px 8px",
        background: "#f8f9fa",
        borderBottom: "1px solid #e5e7eb",
    },
    select: {
        padding: "3px 6px",
        borderRadius: 4,
        border: "1px solid #d1d5db",
        background: "#fff",
        fontSize: 13,
        cursor: "pointer",
        outline: "none",
    },
    divider: {
        width: 1,
        height: 20,
        background: "#d1d5db",
        margin: "0 4px",
    },
    btn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px 6px",
        border: "1px solid transparent",
        borderRadius: 4,
        background: "transparent",
        cursor: "pointer",
        color: "#374151",
        fontSize: 13,
        transition: "background 0.15s, border-color 0.15s",
        minWidth: 28,
        minHeight: 28,
    },
    btnActive: {
        background: "#dbeafe",
        borderColor: "#93c5fd",
        color: "#1d4ed8",
    },
    editor: {
        minHeight: 160,
        padding: "12px 14px",
        outline: "none",
        fontSize: 15,
        lineHeight: 1.7,
        color: "#111827",
        overflowY: "auto",
        wordBreak: "break-word",
        /* Support both Arabic (Noto Naskh) and Latin fonts */
        fontFamily: "'Noto Naskh Arabic', 'Noto Sans Arabic', 'Segoe UI', Tahoma, Arial, sans-serif",
    },
};

/* ─── Global CSS for placeholder + RTL content styling ─── */
const css = `
  [contenteditable][data-placeholder]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
  }
  [contenteditable] blockquote {
    border-inline-start: 3px solid #d1d5db;
    margin: 8px 0;
    padding-inline-start: 12px;
    color: #6b7280;
  }
  [contenteditable] a {
    color: #2563eb;
    text-decoration: underline;
  }
  [contenteditable] ul,
  [contenteditable] ol {
    padding-inline-start: 24px;
    margin: 4px 0;
  }
  [contenteditable][dir="rtl"] ul,
  [contenteditable][dir="rtl"] ol {
    padding-inline-start: 0;
    padding-inline-end: 24px;
  }
  [contenteditable] h1 { font-size: 1.6em; font-weight: 700; margin: 8px 0 4px; }
  [contenteditable] h2 { font-size: 1.3em; font-weight: 700; margin: 8px 0 4px; }
  [contenteditable] h3 { font-size: 1.1em; font-weight: 600; margin: 6px 0 2px; }
  [contenteditable] p  { margin: 4px 0; }
`;

export default RichTextEditor;